// Host + environment resolution — the single source of truth for "where am I
// running and what does that mean". Every domain-derived URL (the marketing
// "Powered by Klimb" link, the studio/app links, a tenant's public URL) and
// the control-plane-vs-tenant routing decision flows from here, so nothing is
// hardcoded to a single environment (no more `klimb.app`).
//
// Topology (mirrors the backend tenancy resolver + infra/stacks/live/klimb):
//   local : apex lvh.me        — app.lvh.me, {slug}.lvh.me, marketing klmb.dev
//   dev   : apex klmb.dev      — app.klmb.dev, {slug}.klmb.dev
//   prod  : apex klimblearn.com— app.klimblearn.com, {slug}.klimblearn.com
//
// lvh.me resolves every label (incl. wildcards) to 127.0.0.1, so tenant
// subdomains work locally with zero hosts-file edits: visit demo.lvh.me:5180.

export type KlimbEnv = 'local' | 'dev' | 'prod'

// Control-plane labels under the apex — platform surfaces, never tenant slugs.
// Mirrors tenancy.DefaultReservedSubdomains in the backend so the client and
// server agree on what is and isn't an academy host.
const RESERVED_LABELS = new Set([
  'www', 'api', 'app', 'admin', 'platform', 'studio', 'klimb', 'blog', 'docs',
  'status', 'help', 'support', 'auth', 'login', 'signup', 'cname',
])

interface EnvProfile {
  env: KlimbEnv
  apex: string
  /** Origin of the marketing site for this env (the "Powered by Klimb" target). */
  marketingUrl: string
  /** Origin of the control-plane SPA (studio/platform/auth) for this env. */
  appUrl: string
}

// Per-environment origins. The app/marketing origins are absolute so links work
// when crossing hosts (a tenant site linking back to studio, the footer linking
// to marketing). Local points at the dev marketing site — there is no local
// marketing build to link to.
const PROFILES: Record<KlimbEnv, EnvProfile> = {
  local: { env: 'local', apex: 'lvh.me', marketingUrl: 'https://klmb.dev', appUrl: 'http://app.lvh.me:5180' },
  dev: { env: 'dev', apex: 'klmb.dev', marketingUrl: 'https://klmb.dev', appUrl: 'https://app.klmb.dev' },
  prod: { env: 'prod', apex: 'klimblearn.com', marketingUrl: 'https://klimblearn.com', appUrl: 'https://app.klimblearn.com' },
}

export interface HostInfo {
  env: KlimbEnv
  apex: string
  /** The bare hostname (no port), lowercased. */
  hostname: string
  /** True on the apex, a reserved subdomain (app/studio/…), or a local/dev host. */
  isControlPlane: boolean
  /** True when the host is a tenant academy ({slug}.apex or a custom domain). */
  isTenantHost: boolean
  /** The tenant slug when on a {slug}.apex host; null for custom domains / control plane. */
  tenantSlug: string | null
  /** Marketing site origin — the only sanctioned external Klimb link. */
  marketingUrl: string
  /** Control-plane SPA origin (studio/platform/auth). */
  appUrl: string
  /** Studio entry point for this environment. */
  studioUrl: string
  /** Where the "Powered by Klimb" footer link points (the marketing site). */
  poweredByUrl: string
}

function profileFor(hostname: string): EnvProfile {
  if (hostname.endsWith('.klimblearn.com') || hostname === 'klimblearn.com') return PROFILES.prod
  if (hostname.endsWith('.klmb.dev') || hostname === 'klmb.dev') return PROFILES.dev
  // localhost, *.lvh.me, *.localhost, 127.0.0.1, and anything unrecognized
  // (custom domains in dev/preview) default to the local profile.
  return PROFILES.local
}

function isLoopback(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.localhost')
  )
}

// resolveHost classifies a hostname into the full HostInfo. Pure + injectable so
// it's unit-testable without a real `window`.
export function resolveHost(rawHostname: string): HostInfo {
  const hostname = (rawHostname || '').trim().toLowerCase()
  const profile = profileFor(hostname)
  const { apex, marketingUrl, appUrl } = profile

  const base: Omit<HostInfo, 'isControlPlane' | 'isTenantHost' | 'tenantSlug'> = {
    env: profile.env,
    apex,
    hostname,
    marketingUrl,
    appUrl,
    studioUrl: `${appUrl}/studio`,
    poweredByUrl: marketingUrl,
  }

  // Loopback + bare apex are always the control plane.
  if (hostname === '' || isLoopback(hostname) || hostname === apex) {
    return { ...base, isControlPlane: true, isTenantHost: false, tenantSlug: null }
  }

  // `{label}.{apex}` — a single non-reserved label is a tenant slug. Reserved
  // labels (app, studio, …) and deeper nesting belong to the control plane.
  const suffix = `.${apex}`
  if (hostname.endsWith(suffix)) {
    const label = hostname.slice(0, -suffix.length)
    if (label === '' || label.includes('.') || RESERVED_LABELS.has(label)) {
      return { ...base, isControlPlane: true, isTenantHost: false, tenantSlug: null }
    }
    return { ...base, isControlPlane: false, isTenantHost: true, tenantSlug: label }
  }

  // Off-apex host that isn't loopback: a tenant custom domain. The slug is
  // resolved server-side from the Host header; the client only needs to know
  // this is a tenant site, not the control plane.
  return { ...base, isControlPlane: false, isTenantHost: true, tenantSlug: null }
}

// currentHost reads window.location at module load. Guarded so tests / SSR that
// import this module without a DOM fall back to the control-plane local profile
// instead of throwing.
export const currentHost: HostInfo = resolveHost(
  typeof window !== 'undefined' && window.location ? window.location.hostname : '',
)
